/**
 * Teacher Schedule API Service
 *
 * Real API client for teacher-facing schedule endpoints.
 * Replaces mockTeacherScheduleService.js for production use.
 */

import tokenManager from './tokenManager';

const API_BASE = process.env.REACT_APP_RESOURCE_API || 'https://neev-api.onrender.com';

/**
 * Get auth headers for API requests
 */
const getAuthHeaders = () => ({
  'Authorization': `Bearer ${tokenManager.getAccessToken()}`,
  'Content-Type': 'application/json',
});

/**
 * Handle API response and throw on error
 */
const handleResponse = async (response) => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `API error: ${response.status}`);
  }
  return response.json();
};

export const teacherScheduleApi = {
  /**
   * Fetch teacher's schedule events for a date range
   * @param {Object} params - Query parameters
   * @param {string} params.startDate - Start date (YYYY-MM-DD)
   * @param {string} params.endDate - End date (YYYY-MM-DD)
   * @returns {Promise<{events: Array, upcoming_count: number}>}
   */
  async listEvents({ startDate, endDate } = {}) {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);

    const url = `${API_BASE}/api/v1/teacher/schedule${params.toString() ? '?' + params.toString() : ''}`;
    const response = await fetch(url, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  /**
   * Get single event details
   * @param {number|string} id - Event occurrence ID
   * @returns {Promise<Object>} Event details
   */
  async getEvent(id) {
    const response = await fetch(`${API_BASE}/api/v1/teacher/schedule/${id}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  /**
   * Submit feedback for an event
   * @param {number|string} eventId - Event occurrence ID
   * @param {Object} feedback - Feedback data
   * @param {string} feedback.status - 'done' or 'not_done'
   * @param {string} feedback.comment - Feedback comment
   * @returns {Promise<Object>} Updated event
   */
  async submitFeedback(eventId, { status, comment }) {
    const response = await fetch(`${API_BASE}/api/v1/teacher/schedule/${eventId}/feedback`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ status, comment }),
    });
    return handleResponse(response);
  },

  /**
   * Update existing feedback for an event
   * @param {number|string} eventId - Event occurrence ID
   * @param {Object} feedback - Feedback data
   * @param {string} feedback.status - 'done' or 'not_done'
   * @param {string} feedback.comment - Feedback comment
   * @returns {Promise<Object>} Updated event
   */
  async updateFeedback(eventId, { status, comment }) {
    const response = await fetch(`${API_BASE}/api/v1/teacher/schedule/${eventId}/feedback`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ status, comment }),
    });
    return handleResponse(response);
  },

  /**
   * Get count of upcoming scheduled events
   * @returns {Promise<{count: number}>}
   */
  async getUpcomingCount() {
    const response = await fetch(`${API_BASE}/api/v1/teacher/schedule/upcoming-count`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  // =============================================
  // NOTIFICATION METHODS
  // =============================================

  /**
   * Get notifications for the current user
   * @param {Object} params - Query parameters
   * @param {boolean} params.unreadOnly - Only fetch unread notifications
   * @param {number} params.limit - Max notifications to fetch
   * @returns {Promise<{notifications: Array}>}
   */
  async listNotifications({ unreadOnly = false, limit = 50, offset = 0 } = {}) {
    const params = new URLSearchParams();
    if (unreadOnly) params.append('unread_only', 'true');
    if (limit) params.append('limit', limit.toString());
    if (offset) params.append('offset', offset.toString());

    const url = `${API_BASE}/api/v1/teacher/schedule/notifications${params.toString() ? '?' + params.toString() : ''}`;
    const response = await fetch(url, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  /**
   * Get count of unread notifications
   * @returns {Promise<{count: number}>}
   */
  async getNotificationCount() {
    const response = await fetch(`${API_BASE}/api/v1/teacher/schedule/notifications/count`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  /**
   * Mark a notification as read
   * @param {number|string} notificationId - Notification ID
   * @returns {Promise<{message: string}>}
   */
  async markNotificationRead(notificationId) {
    const response = await fetch(`${API_BASE}/api/v1/teacher/schedule/notifications/${notificationId}/read`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  /**
   * Mark all notifications as read
   * @returns {Promise<{message: string, count_marked: number}>}
   */
  async markAllNotificationsRead() {
    const response = await fetch(`${API_BASE}/api/v1/teacher/schedule/notifications/read-all`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  // =============================================
  // LEAVE REQUEST METHODS
  // =============================================

  /**
   * Submit a leave request
   * @param {Object} leaveData - Leave request data
   * @param {string} leaveData.startAt - Start datetime (ISO 8601)
   * @param {string} leaveData.endAt - End datetime (ISO 8601)
   * @param {string} leaveData.reason - Reason for leave
   * @param {string} leaveData.leaveType - Type: personal, medical, emergency, other
   * @returns {Promise<{request: Object, conflicts: Array, message: string}>}
   */
  async submitLeave({ startAt, endAt, reason, leaveType = 'personal' }) {
    const response = await fetch(`${API_BASE}/api/v1/teacher/schedule/leave`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        start_at: startAt,
        end_at: endAt,
        reason,
        leave_type: leaveType,
      }),
    });
    return handleResponse(response);
  },

  /**
   * Get user's leave requests
   * @param {Object} params - Query parameters
   * @param {number} params.limit - Max requests to fetch
   * @param {number} params.offset - Pagination offset
   * @returns {Promise<{requests: Array, total: number}>}
   */
  async listMyLeave({ limit = 50, offset = 0 } = {}) {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (offset) params.append('offset', offset.toString());

    const url = `${API_BASE}/api/v1/teacher/schedule/leave${params.toString() ? '?' + params.toString() : ''}`;
    const response = await fetch(url, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  /**
   * Cancel a pending leave request
   * @param {number|string} leaveId - Leave request ID
   * @returns {Promise<{message: string}>}
   */
  async cancelLeave(leaveId) {
    const response = await fetch(`${API_BASE}/api/v1/teacher/schedule/leave/${leaveId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  // =============================================
  // EXAM STATUS METHODS
  // =============================================

  /**
   * Get exam status for a scheduler event
   * @param {number|string} eventId - Event occurrence ID
   * @returns {Promise<Object>} Exam status info
   */
  async getExamStatus(eventId) {
    const response = await fetch(`${API_BASE}/api/v1/teacher/schedule/${eventId}/exam-status`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  /**
   * Mark an exam event as conducted
   * @param {number|string} eventId - Event occurrence ID
   * @returns {Promise<{status: string, message: string, exam: Object}>}
   */
  async markExamConducted(eventId) {
    const response = await fetch(`${API_BASE}/api/v1/teacher/schedule/${eventId}/mark-conducted`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  /**
   * Cancel an exam event with reason
   * @param {number|string} eventId - Event occurrence ID
   * @param {string} reason - Cancellation reason (required)
   * @returns {Promise<{status: string, message: string, exam: Object}>}
   */
  async cancelExam(eventId, reason) {
    const response = await fetch(`${API_BASE}/api/v1/teacher/schedule/${eventId}/cancel-exam`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ reason }),
    });
    return handleResponse(response);
  },
};

export default teacherScheduleApi;
